import { Request, Response, NextFunction } from 'express'
import { AuthError, isAuthError, formatErrorResponse, getStatusCode } from '../errors'

/**
 * Express error handling middleware
 * Catches all errors and formats them consistently
 */
export function errorHandler(
  error: unknown,
  req: Request,
  res: Response,
  next: NextFunction
) {
  // Log error
  if (error instanceof Error) {
    console.error('[Auth Error]', {
      code: isAuthError(error) ? error.code : 'UNKNOWN',
      message: error.message,
      path: req.path,
      method: req.method,
      timestamp: new Date().toISOString()
    })
  }

  // Get formatted error
  const statusCode = getStatusCode(error)
  const errorResponse = formatErrorResponse(error)

  // Don't expose internal error details in production
  if (process.env.NODE_ENV === 'production' && !isAuthError(error)) {
    return res.status(500).json({
      error: 'INTERNAL_ERROR',
      message: 'An internal error occurred',
      statusCode: 500
    })
  }

  // Send error response
  res.status(statusCode).json(errorResponse)
}

/**
 * Async route wrapper to catch errors
 */
export function asyncHandler(
  fn: (req: Request, res: Response, next: NextFunction) => Promise<any>
) {
  return (req: Request, res: Response, next: NextFunction) => {
    Promise.resolve(fn(req, res, next)).catch(next)
  }
}

/**
 * Not found handler - must be registered after all routes
 */
export function notFoundHandler(req: Request, res: Response) {
  res.status(404).json({
    error: 'NOT_FOUND',
    message: `Route ${req.method} ${req.path} not found`,
    statusCode: 404
  })
}

/**
 * Validation error handler
 * Catches validation errors from libraries like joi, yup, etc
 */
export function validationErrorHandler(
  error: any,
  req: Request,
  res: Response,
  next: NextFunction
) {
  // joi error
  if (error?.isJoi) {
    return res.status(400).json({
      error: 'VALIDATION_ERROR',
      message: 'Validation failed',
      statusCode: 400,
      details: error.details?.map((d: any) => ({
        field: d.path.join('.'),
        message: d.message,
        type: d.type
      }))
    })
  }

  // yup error
  if (error?.name === 'ValidationError') {
    return res.status(400).json({
      error: 'VALIDATION_ERROR',
      message: 'Validation failed',
      statusCode: 400,
      details: error.errors
    })
  }

  next(error)
}

/**
 * CORS error handler
 */
export function corsErrorHandler(
  error: any,
  req: Request,
  res: Response,
  next: NextFunction
) {
  if (error?.message?.includes('CORS')) {
    return res.status(403).json({
      error: 'CORS_ERROR',
      message: 'CORS policy violation',
      statusCode: 403
    })
  }

  next(error)
}

/**
 * Rate limit error handler
 */
export function rateLimitErrorHandler(
  error: any,
  req: Request,
  res: Response,
  next: NextFunction
) {
  // express-rate-limit error
  if (error?.message?.includes('rate limit')) {
    const retryAfter = error.retryAfter || 60
    res.set('Retry-After', String(retryAfter))

    return res.status(429).json({
      error: 'RATE_LIMIT_EXCEEDED',
      message: 'Too many requests',
      statusCode: 429,
      details: {
        retryAfter
      }
    })
  }

  next(error)
}

/**
 * Multi-layer error handling middleware setup
 * Install in order: validation -> cors -> rateLimit -> auth -> generic
 */
export function setupErrorHandling(app: any) {
  // Validation errors
  app.use(validationErrorHandler)

  // CORS errors
  app.use(corsErrorHandler)

  // Rate limit errors
  app.use(rateLimitErrorHandler)

  // 404 Not Found (before generic error handler)
  app.use(notFoundHandler)

  // Generic error handler (must be last)
  app.use(errorHandler)
}
